import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { type ColumnDef } from '@tanstack/react-table';
import { Property } from '@property/shared';
import { MapPin, AlertCircle, ShieldCheck, ShieldOff, ShieldAlert } from 'lucide-react';
import { DataTable, DataTableColumnHeader } from '@/components/data-table';
import { createdAtColumn } from '@/components/data-table/columns';
import { AddPropertyButton } from '@/components/add-property-button';
import { useProperties, useAllProperties } from '@/hooks/use-property-queries';
import { useTableRefresh } from '@/hooks/use-table-refresh';
import { queryKeys } from '@/lib/query-keys';

const PAGE_SIZE = 10;

// Cell renderers receive row.original via TanStack Table context — no closure over component state,
// so this is safe at module scope and avoids recreating the array on every render.
const columns: ColumnDef<Property>[] = [
  {
    accessorKey: 'name',
    enableSorting: true,
    meta: { label: 'Property', isPrimary: true, isMandatory: true, searchable: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Property" className="pl-6" />
    ),
    cell: ({ row }) => {
      const { name, address, subscription } = row.original;
      const status = subscription.status;
      const [StatusIcon, iconClass, statusLabel] =
        status === 'ACTIVE'
          ? ([ShieldCheck, 'text-sidebar-primary', 'Monitoring active'] as const)
          : status === 'STOPPED'
            ? ([ShieldOff, 'text-foreground/25', 'Monitoring stopped'] as const)
            : ([ShieldAlert, 'text-ring', 'Monitoring expired'] as const);
      return (
        <div className="pl-6 pr-3 flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-sm group-hover:text-primary transition-colors line-clamp-1 capitalize">
              {name}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">
              {address.line1}
              {address.line2 ? `, ${address.line2}` : ''}, {address.city}
            </div>
          </div>
          <StatusIcon aria-label={statusLabel} className={`size-4 flex-shrink-0 ${iconClass}`} />
        </div>
      );
    },
  },
  {
    id: 'dueDate',
    enableSorting: false,
    meta: { label: 'Due Date', defaultHidden: true },
    header: () => <span className="text-sm font-medium">Due Date</span>,
    cell: ({ row }) => {
      const dueDate = row.original.subscription.nextInspectionDue;
      return <span className="text-sm text-muted-foreground">{dueDate || '—'}</span>;
    },
  },
  {
    id: 'planType',
    accessorFn: (row) => row.subscription.planType,
    enableSorting: false,
    meta: { label: 'Plan', defaultHidden: true },
    header: () => <span className="text-sm font-medium">Plan</span>,
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground capitalize">
        {row.original.subscription.planType ?? '—'}
      </span>
    ),
  },
  {
    id: 'autoRenewal',
    accessorFn: (row) => row.subscription.isAutoRenewalEnabled,
    enableSorting: false,
    meta: { label: 'Auto-renewal', defaultHidden: true },
    header: () => <span className="text-sm font-medium">Auto-renewal</span>,
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">
        {row.original.subscription.isAutoRenewalEnabled ? 'On' : 'Off'}
      </span>
    ),
  },
  createdAtColumn<Property>((row) => row.createdAt),
];

export function PropertyTable({ adminView = false }: { adminView?: boolean }) {
  const navigate = useNavigate();
  const refresh = useTableRefresh(
    adminView ? queryKeys.properties.adminAll() : queryKeys.properties.all(),
  );
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string | undefined>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState<string | undefined>();

  const userResult = useProperties({
    viewPage: page,
    viewPageSize: PAGE_SIZE,
    sortBy,
    sortOrder,
    search,
    enabled: !adminView,
  });
  const adminResult = useAllProperties({
    viewPage: page,
    viewPageSize: PAGE_SIZE,
    sortBy,
    sortOrder,
    search,
    enabled: adminView,
  });
  const { data, total, isLoading, isFetching, isError } = adminView ? adminResult : userResult;

  return (
    <DataTable
      tableId="property-table"
      showColumnToggle
      onRefresh={refresh}
      columns={columns}
      data={data}
      isLoading={isLoading}
      isFetching={isFetching}
      pagination={{ page, pageSize: PAGE_SIZE, total, onPageChange: setPage }}
      sorting={{
        sortBy,
        sortOrder,
        onSortChange: (by, order) => {
          setSortBy(by);
          setSortOrder(order ?? 'desc');
        },
      }}
      onSearchChange={(q) => {
        setSearch(q || undefined);
        setPage(1);
      }}
      emptyState={
        isError
          ? {
              icon: <AlertCircle className="h-6 w-6 text-muted-foreground" />,
              title: 'Failed to load properties',
              description: 'There was a problem fetching your properties. Please try again.',
            }
          : {
              icon: <MapPin className="h-6 w-6 text-muted-foreground" />,
              title: 'No properties yet',
              description: 'Add your first property to start monitoring it.',
              action: <AddPropertyButton />,
            }
      }
      getRowId={(row) => row.propertyId}
      onRowClick={(row) =>
        navigate({
          to: '/dashboard/land-monitoring/$propertyId',
          params: { propertyId: row.propertyId },
        })
      }
    />
  );
}
